import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ChevronDown, ChevronUp, Play, VideoOff, Plus, CalendarDays, Check, Undo2, X, Sparkles } from "lucide-react";
import { api, errMsg, uploadPhoto, photoUrl, getProfile } from "./api";
import { Shell, Panel, BigBtn, GhostBtn, Spinner, inputCls } from "./ui";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SignOff from "./SignOff";

const REASONS = ["No supplies", "Equipment fault", "Access unavailable", "Ran out of time", "Other"];
const STATUS_LABEL = { done: "Done", cannot: "Cannot complete", na: "Not applicable" };
const SECTIONS = [
  { key: "before_class", label: "Before class", short: "Before class" },
  { key: "during_class", label: "Whilst class is on", short: "During class" },
  { key: "anytime", label: "Anytime during your shift", short: "Anytime" },
  { key: "end_of_shift", label: "End of shift", short: "End of shift" },
];

function SaveDot({ state }) {
  if (!state) return null;
  const map = { saving: ["bg-amber-400", "Saving…"], saved: ["bg-emerald-400", "Saved"], error: ["bg-[#ff2e4d]", "Not saved"] };
  const [c, t] = map[state];
  return <span className="inline-flex items-center gap-1 text-[10px] text-white/40"><span className={`h-1.5 w-1.5 rounded-full ${c}`} />{t}</span>;
}

function TaskRow({ task, videos, readOnly, saveState, onPatch, onPhoto, onRemovePhoto, chlorine, onChlorine, compact }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(task.note || "");
  const [reason, setReason] = useState(task.reason || "");
  const [reading, setReading] = useState("");
  const [product, setProduct] = useState("");
  const video = task.video_id ? videos.find((v) => v.video_id === task.video_id) : null;
  const isDone = task.status === "done";
  const isException = task.status === "cannot" || task.status === "na";
  const isChlorine = task.chlorine;
  const chlorineDone = isChlorine && chlorine?.routine;

  const toggleDone = () => {
    if (readOnly) return;
    if (isChlorine && !chlorineDone && !isDone) {
      setOpen(true);
      toast.info("Record the routine chlorine check in the details below");
      return;
    }
    onPatch(task, { status: isDone ? "todo" : "done", rev: task.rev });
  };

  const hint = isChlorine
    ? (chlorineDone ? `Routine recorded by ${chlorine.routine.by} at ${chlorine.routine.at_local.slice(11)}` : "Routine dose outstanding today")
    : task.conditional ? "Done = checked — action only if it was needed"
    : task.ongoing ? "Done = attended to during this shift" : null;

  return (
    <Panel className={`overflow-hidden ${isDone ? "opacity-80" : ""}`} data-testid={`task-row-${task.task_id}`}>
      <div className="flex items-stretch">
        <button
          data-testid={`task-done-toggle-${task.task_id}`}
          onClick={toggleDone}
          disabled={readOnly}
          aria-label={isDone ? "Undo done" : "Mark done"}
          className={`${compact ? "w-12 min-h-[52px]" : "w-16 min-h-[60px]"} flex items-center justify-center border-r border-white/10 transition active:scale-95 ${
            isDone ? "bg-[#ff2e4d] text-black" : isException ? "bg-amber-500/20 text-amber-400" : "bg-black/30 text-white/30"
          }`}
        >
          {isException ? <X size={20} /> : <Check size={22} />}
        </button>
        <div className="flex-1 py-2.5 px-3 min-w-0">
          <p className={`text-[15px] leading-snug ${isDone ? "line-through text-white/50" : ""}`}>{task.title}</p>
          <p className="text-xs text-white/40 mt-0.5">
            {hint ? hint : task.class_label ? task.class_label : ""}
            {task.done_by && task.status !== "todo" ? `${hint || task.class_label ? " · " : ""}${STATUS_LABEL[task.status]} by ${task.done_by}` : ""}
          </p>
          <SaveDot state={saveState} />
        </div>
        <div className="flex items-center pr-1">
          {video ? (
            <a data-testid={`task-video-${task.task_id}`} href={video.url} target="_blank" rel="noreferrer"
              className="h-11 min-w-[44px] px-1 flex flex-col items-center justify-center text-[#ff2e4d]">
              <Play size={17} /><span className="text-[9px] uppercase">Watch</span>
            </a>
          ) : (
            <span data-testid={`task-video-pending-${task.task_id}`} className="h-11 min-w-[44px] px-1 flex flex-col items-center justify-center text-white/25">
              <VideoOff size={17} /><span className="text-[9px] uppercase leading-tight text-center">Video pending</span>
            </span>
          )}
          <button data-testid={`task-details-${task.task_id}`} onClick={() => setOpen((v) => !v)}
            aria-label="Details" className="h-11 w-11 flex items-center justify-center text-white/50">
            {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-white/10 p-4 space-y-3 bg-black/20">
          {(task.reason || isException) && (
            <p className="text-amber-400 text-sm">{STATUS_LABEL[task.status] || ""}{task.reason ? ` — ${task.reason}` : ""}</p>
          )}
          {isChlorine && !readOnly && (
            <div className="space-y-2 rounded-xl border border-sky-400/30 bg-sky-400/5 p-3">
              {chlorineDone ? (
                <>
                  <p className="text-sm text-sky-300">Routine dose recorded today by {chlorine.routine.by} at {chlorine.routine.at_local}.{chlorine.routine.reading ? ` Reading: ${chlorine.routine.reading}.` : ""}{chlorine.routine.product_quantity ? ` Used: ${chlorine.routine.product_quantity}.` : ""}</p>
                  {chlorine.additional?.length > 0 && <p className="text-xs text-white/50">{chlorine.additional.length} additional dose(s) logged.</p>}
                </>
              ) : (
                <p className="text-sm text-sky-300">Record the routine daily check per the venue's approved procedure. Tracked once per venue day across all shifts.</p>
              )}
              <input data-testid="chlorine-reading-input" value={reading} onChange={(e) => setReading(e.target.value)} placeholder="Reading (per approved procedure)" className={inputCls} />
              <input data-testid="chlorine-product-input" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Product & quantity used (per approved procedure)" className={inputCls} />
              <div className="flex gap-2 flex-wrap">
                {!chlorineDone && (
                  <GhostBtn data-testid="chlorine-record-routine" className="border-sky-400/50 text-sky-300"
                    onClick={() => onChlorine(task, "routine", reading, product)}>Record routine completion</GhostBtn>
                )}
                {chlorineDone && (
                  <GhostBtn data-testid="chlorine-record-additional"
                    onClick={() => onChlorine(task, "additional", reading, product)}>Log additional dose</GhostBtn>
                )}
              </div>
            </div>
          )}
          {!readOnly && (
            <>
              <textarea data-testid={`task-note-${task.task_id}`} value={note} onChange={(e) => setNote(e.target.value)}
                onBlur={() => note !== (task.note || "") && onPatch(task, { note })}
                placeholder="Optional note…" rows={2} className={inputCls + " py-3 min-h-[60px]"} />
              <div className="flex flex-wrap gap-2 items-center">
                <select data-testid={`task-reason-${task.task_id}`} value={reason} onChange={(e) => setReason(e.target.value)}
                  className={inputCls + " flex-1 min-w-[150px]"}>
                  <option value="">Reason (for exceptions)…</option>
                  {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <GhostBtn data-testid={`task-cannot-${task.task_id}`}
                  onClick={() => reason ? onPatch(task, { status: "cannot", reason, rev: task.rev }) : toast.error("Pick a reason first")}
                  className="text-amber-400 border-amber-400/40">Cannot complete</GhostBtn>
                {task.status !== "todo" && (
                  <GhostBtn data-testid={`task-undo-${task.task_id}`} onClick={() => onPatch(task, { status: "todo", rev: task.rev })}>
                    <Undo2 size={15} className="inline mr-1" />Undo
                  </GhostBtn>
                )}
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-white/60 min-h-[44px] cursor-pointer">
                <Camera size={18} />
                <span>Add photo</span>
                <input data-testid={`task-photo-input-${task.task_id}`} type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files?.[0] && onPhoto(task, e.target.files[0])} />
              </label>
            </>
          )}
          {task.photos?.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {task.photos.map((pid) => (
                <div key={pid} className="relative">
                  <img src={photoUrl(pid)} alt="task evidence" className="h-16 w-16 object-cover rounded-lg border border-white/15" />
                  {!readOnly && (
                    <button onClick={() => onRemovePhoto(task, pid)} aria-label="Remove photo"
                      className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-black border border-white/30 flex items-center justify-center">
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}

function BigCleanCard({ title, steps, rowProps }) {
  const finalised = steps.filter((t) => t.status !== "todo").length;
  const exceptions = steps.filter((t) => t.status === "cannot" || t.status === "na").length;
  const complete = finalised === steps.length;
  const verdict = complete
    ? (exceptions > 0 ? "Completed with exception" : "Fully completed")
    : `${finalised} of ${steps.length} steps`;
  const groups = [];
  for (const t of steps) {
    const g = t.step_group || null;
    const last = groups[groups.length - 1];
    if (last && last.group === g) last.items.push(t);
    else groups.push({ group: g, items: [t] });
  }
  return (
    <div data-testid="big-clean-card"
      className="rounded-2xl border-2 border-[#ff2e4d]/60 bg-[#ff2e4d]/[0.06] p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#ff2e4d] font-bold">
            <Sparkles size={12} /> This shift's big clean
          </p>
          <h4 className="text-xl uppercase italic mt-1" style={{ fontFamily: "Anton, sans-serif" }}>{title}</h4>
          <p className="text-xs text-white/50">Complete once during this shift.</p>
        </div>
        <span data-testid="big-clean-verdict" className={`text-xs rounded-full px-2.5 py-1 border whitespace-nowrap ${
          complete ? (exceptions ? "border-amber-400 text-amber-400" : "border-emerald-400 text-emerald-400") : "border-white/25 text-white/60"}`}>
          {verdict}
        </span>
      </div>
      {groups.map((g, i) => (
        <div key={i} className="space-y-2">
          {g.group && <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 pt-1">{g.group}</p>}
          {g.items.map((t) => <TaskRow key={t.task_id} task={t} compact {...rowProps} />)}
        </div>
      ))}
    </div>
  );
}

function fmtDate(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function DatePopover({ open, onOpenChange, selected, onPick, children }) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0 bg-[#141414] border-white/15 text-white rounded-2xl">
        <Calendar
          mode="single"
          selected={selected ? new Date(`${selected}T00:00:00`) : undefined}
          onSelect={(d) => d && onPick(fmtDate(d))}
          className="bg-transparent text-white"
          data-testid="schedule-calendar"
        />
      </PopoverContent>
    </Popover>
  );
}

export default function Checklist() {
  const isTrainer = getProfile()?.role === "trainer";
  const [today, setToday] = useState(null);
  const [shift, setShift] = useState(null);
  const [videos, setVideos] = useState([]);
  const [chlorine, setChlorine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [saveStates, setSaveStates] = useState({});
  const [browseDate, setBrowseDate] = useState("");
  const [browsing, setBrowsing] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [calOpenTop, setCalOpenTop] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (isTrainer) { setLoading(false); return; }
    Promise.all([api.get("/shifts/today"), api.get("/videos")])
      .then(([t, v]) => { setToday(t.data); setVideos(v.data.videos); })
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShift = async (shiftType) => {
    setLoading(true);
    try {
      const { data } = await api.post("/shifts/open", { shift_type: shiftType });
      setShift(data);
      setChlorine(data.chlorine_today);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };

  // Weekend: auto-select the single shift
  useEffect(() => {
    if (today?.auto && !shift && !loading) openShift(today.auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today]);

  const mark = (id, state) => {
    setSaveStates((s) => ({ ...s, [id]: state }));
    if (state === "saved") setTimeout(() => setSaveStates((s) => ({ ...s, [id]: null })), 2000);
  };

  const patchTask = async (task, body) => {
    mark(task.task_id, "saving");
    try {
      const { data } = await api.patch(`/tasks/${task.task_id}`, body);
      setShift((s) => ({ ...s, tasks: s.tasks.map((t) => (t.task_id === task.task_id ? data : t)) }));
      mark(task.task_id, "saved");
    } catch (e) { mark(task.task_id, "error"); toast.error(errMsg(e)); }
  };

  const recordChlorine = async (task, type, reading, product) => {
    try {
      const { data } = await api.post("/chlorine", { type, reading, product_quantity: product });
      setChlorine(data.state);
      toast.success(type === "routine" ? "Routine chlorine recorded" : "Additional dose logged");
      if (type === "routine" && task.status === "todo") patchTask(task, { status: "done", rev: task.rev });
    } catch (e) {
      toast.warning(errMsg(e));
      try { const { data } = await api.get("/chlorine/today"); setChlorine(data); } catch {}
    }
  };

  const addPhoto = async (task, file) => {
    try {
      const pid = await uploadPhoto(file, `task:${task.task_id}`);
      await api.post(`/tasks/${task.task_id}/photos/${pid}`);
      setShift((s) => ({ ...s, tasks: s.tasks.map((t) => t.task_id === task.task_id ? { ...t, photos: [...(t.photos || []), pid] } : t) }));
      toast.success("Photo saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removePhoto = async (task, pid) => {
    try {
      await api.delete(`/tasks/${task.task_id}/photos/${pid}`);
      setShift((s) => ({ ...s, tasks: s.tasks.map((t) => t.task_id === task.task_id ? { ...t, photos: t.photos.filter((x) => x !== pid) } : t) }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addSession = async () => {
    const label = window.prompt("Label this session (e.g. 9:15am Rhythm Ride)");
    if (!label) return;
    try {
      const { data } = await api.post(`/shifts/${shift.shift_id}/session`, { label });
      setShift(data);
      setChlorine(data.chlorine_today);
      toast.success(`Session "${label}" added — prep and during-class tasks repeated`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const browse = async (date) => {
    const d = date || browseDate;
    if (!d) return;
    try {
      const { data } = await api.get(`/shifts/browse?date=${d}`);
      setBrowsing(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const pickDate = (d) => {
    setBrowseDate(d);
    setCalOpen(false);
    setCalOpenTop(false);
    browse(d);
  };

  const bySection = useMemo(() => {
    const out = {};
    for (const s of SECTIONS) out[s.key] = (shift?.tasks || []).filter((t) => t.section === s.key);
    return out;
  }, [shift]);

  const total = shift?.tasks.length || 0;
  const finalised = shift?.tasks.filter((t) => t.status !== "todo").length || 0;
  const submitted = shift?.status === "submitted";
  const shiftLabel = shift && (today?.shifts.find((s) => s.key === shift.shift_type)?.label || shift.shift_type);

  const rowProps = {
    videos, readOnly: submitted, onPatch: patchTask, onPhoto: addPhoto,
    onRemovePhoto: removePhoto, chlorine, onChlorine: recordChlorine,
  };

  if (loading) return <Shell title="My work schedule" back="/staff"><Spinner /></Shell>;

  if (isTrainer) {
    return (
      <Shell title="My work schedule" back="/staff">
        <Panel className="p-6 text-center space-y-2" data-testid="trainer-no-checklist">
          <p className="font-semibold text-white/80">The cleaning & admin checklist is for Admin shifts</p>
          <p className="text-sm text-white/45">As a Trainer you can still report bike issues and request sundries from the home screen.</p>
        </Panel>
      </Shell>
    );
  }

  if (signing && shift) {
    return <SignOff shift={shift} onBack={() => setSigning(false)}
      onSubmitted={(s) => { setShift(s); setChlorine(s.chlorine_today); setSigning(false); }} />;
  }

  if (!shift) {
    return (
      <Shell title="My work schedule" back="/staff">
        <DatePopover open={calOpenTop} onOpenChange={setCalOpenTop} selected={browseDate || today?.business_date} onPick={pickDate}>
          <button data-testid="shift-date" className="text-white/55 text-sm mb-1 min-h-[44px] underline decoration-white/25 underline-offset-4">
            {today?.business_date} · {today?.perth_time} Perth time
          </button>
        </DatePopover>
        <h2 className="text-2xl uppercase italic mb-5" style={{ fontFamily: "Anton, sans-serif" }}>
          {today?.shifts.length > 1 ? "Confirm your shift" : "Today's shift"}
        </h2>
        <div className="space-y-3" data-testid="shift-picker">
          {today?.shifts.map((s) => (
            <button key={s.key} data-testid={`shift-pick-${s.key}`} onClick={() => openShift(s.key)}
              className={`w-full text-left rounded-2xl border p-5 flex items-center justify-between active:scale-[0.99] transition ${
                today.suggestion === s.key ? "border-[#ff2e4d] bg-[#ff2e4d]/10" : "border-white/15 bg-[#141414]"}`}>
              <span>
                <span className="block text-xl uppercase" style={{ fontFamily: "Anton, sans-serif" }}>{s.label}</span>
                <span className="block text-sm text-white/45">
                  {s.configured && s.start ? `Between ${s.start} – ${s.end}` : "Times not configured yet"}
                  {today.suggestion === s.key ? " · suggested for right now" : ""}
                </span>
              </span>
              {today.existing.find((e) => e.shift_type === s.key) && (
                <span className="text-xs text-white/50 border border-white/20 rounded-full px-2.5 py-1">
                  {today.existing.find((e) => e.shift_type === s.key).status === "submitted" ? "Signed off" : "In progress"}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="mt-8">
          <p className="text-xs uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2"><CalendarDays size={14} /> View another day / shift (view only)</p>
          <DatePopover open={calOpen} onOpenChange={setCalOpen} selected={browseDate} onPick={pickDate}>
            <button data-testid="browse-date-button" className={inputCls + " flex items-center justify-between text-left"}>
              <span className={browseDate ? "" : "text-white/35"}>{browseDate || "Pick a date…"}</span>
              <CalendarDays size={18} className="text-white/40" />
            </button>
          </DatePopover>
          {browsing && (
            <div className="mt-4 space-y-3" data-testid="browse-results">
              <p className="text-xs text-amber-400">Viewing only — this does not change your working shift.</p>
              {browsing.shifts.length === 0 && <p className="text-white/40 text-sm">No shifts recorded on {browsing.date}.</p>}
              {browsing.shifts.map((s) => (
                <Panel key={s.shift_id} className="p-4">
                  <p className="font-semibold capitalize">{s.shift_type} ({s.role || "staff"}) — {s.status === "submitted" ? `Signed off (${s.report_ref})` : "Not signed off"}</p>
                  <p className="text-sm text-white/45">{s.tasks.filter((t) => t.status === "done").length}/{s.tasks.length} done · view only</p>
                </Panel>
              ))}
            </div>
          )}
        </div>
      </Shell>
    );
  }

  if (total === 0) {
    return (
      <Shell title={`${shiftLabel} shift`} back="/staff">
        <Panel className="p-6 text-center" data-testid="no-tasks-panel">
          <p className="font-semibold text-white/70">No tasks configured for your role yet</p>
          <p className="text-sm text-white/40 mt-1">Management can add {shift.role} tasks in the manager console.</p>
          <GhostBtn data-testid="change-shift-button" onClick={() => setShift(null)} className="mt-4">Change shift</GhostBtn>
        </Panel>
      </Shell>
    );
  }

  return (
    <Shell title={`${shiftLabel} shift`} back="/staff">
      <div className="flex items-center justify-between mb-2">
        <p className="text-white/55 text-sm" data-testid="checklist-date">
          {shift.business_date} · {shift.participants.map((p) => p.name).join(", ")}
        </p>
        {today?.shifts.length > 1 && (
          <button data-testid="change-shift-button" onClick={() => setShift(null)} className="text-xs text-white/40 underline underline-offset-4 min-h-[44px]">Change shift</button>
        )}
      </div>

      {/* Section shortcuts */}
      <div className="sticky top-14 z-30 bg-[#050505]/95 backdrop-blur -mx-4 px-4 py-2 border-b border-white/10 mb-4">
        <div className="flex gap-1.5 overflow-x-auto" data-testid="section-shortcuts">
          {SECTIONS.map((s) => {
            const ts = bySection[s.key];
            if (!ts.length) return null;
            const d = ts.filter((t) => t.status !== "todo").length;
            return (
              <button key={s.key} data-testid={`jump-${s.key}`}
                onClick={() => sectionRefs.current[s.key]?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className={`min-h-[40px] px-3 rounded-full border text-xs whitespace-nowrap ${d === ts.length ? "border-emerald-400/50 text-emerald-400" : "border-white/15 text-white/60"}`}>
                {s.short} {d}/{ts.length}
              </button>
            );
          })}
        </div>
        <div className="h-1 rounded-full bg-white/10 overflow-hidden mt-2">
          <div className="h-full bg-[#ff2e4d] transition-all" style={{ width: `${total ? (finalised / total) * 100 : 0}%` }} />
        </div>
      </div>

      {submitted && (
        <Panel className="p-5 mb-5 border-[#ff2e4d]/50" data-testid="shift-submitted-banner">
          <p className="text-lg uppercase" style={{ fontFamily: "Anton, sans-serif" }}>Shift signed off</p>
          <p className="text-sm text-white/55">Report {shift.report_ref} · signed by {shift.signed_by?.name}. Tasks are now locked.</p>
        </Panel>
      )}

      <div className="space-y-7">
        {SECTIONS.map((sec) => {
          const ts = bySection[sec.key];
          if (!ts.length) return null;
          const d = ts.filter((t) => t.status !== "todo").length;
          const isCollapsed = collapsed[sec.key];
          const bigCleans = sec.key === "anytime" ? ts.filter((t) => t.category === "big_clean") : [];
          const normal = ts.filter((t) => t.category !== "big_clean");
          const bcTitles = [...new Set(bigCleans.map((t) => t.big_clean_title))];
          // group normal tasks: by session label first (during/before), then group
          const chunks = [];
          for (const t of normal) {
            const key = t.class_label ? `Session — ${t.class_label}` : (t.group || null);
            const last = chunks[chunks.length - 1];
            if (last && last.key === key) last.items.push(t);
            else chunks.push({ key, items: [t] });
          }
          return (
            <section key={sec.key} ref={(el) => (sectionRefs.current[sec.key] = el)} style={{ scrollMarginTop: "120px" }}>
              <button data-testid={`section-heading-${sec.key}`} onClick={() => setCollapsed((c) => ({ ...c, [sec.key]: !c[sec.key] }))}
                className="w-full flex items-center justify-between min-h-[44px] mb-2.5">
                <h3 className="text-lg uppercase italic" style={{ fontFamily: "Anton, sans-serif" }}>{sec.label}</h3>
                <span className="flex items-center gap-2 text-xs text-white/50">
                  <span data-testid={`section-progress-${sec.key}`} className={d === ts.length ? "text-emerald-400" : ""}>{d}/{ts.length}</span>
                  {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </span>
              </button>
              {!isCollapsed && (
                <div className="space-y-2.5">
                  {bcTitles.map((title) => (
                    <BigCleanCard key={title} title={title}
                      steps={bigCleans.filter((t) => t.big_clean_title === title)} rowProps={rowProps} />
                  ))}
                  {chunks.map((ch, i) => (
                    <div key={i} className="space-y-2.5">
                      {ch.key && <p className="text-[11px] uppercase tracking-[0.2em] text-white/45 pt-2">{ch.key}</p>}
                      {ch.items.map((t) => (
                        <TaskRow key={t.task_id} task={t} saveState={saveStates[t.task_id]} {...rowProps} />
                      ))}
                    </div>
                  ))}
                  {sec.key === "during_class" && !submitted && (
                    <button data-testid="add-session-button" onClick={addSession}
                      className="w-full min-h-[48px] rounded-xl border border-dashed border-white/25 text-white/55 text-sm flex items-center justify-center gap-2 active:bg-white/5">
                      <Plus size={16} /> Add another session (repeats prep & during-class tasks)
                    </button>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-[#050505] via-[#050505]/95 to-transparent pt-6"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
          <div className="mx-auto max-w-xl px-4">
            <BigBtn data-testid="review-sign-button" onClick={() => setSigning(true)}>
              Review & sign {finalised < total ? `(${total - finalised} left)` : ""}
            </BigBtn>
          </div>
        </div>
      )}
      <div className="h-6" />
    </Shell>
  );
}
